import { mdiChevronUp, mdiChevronDown, mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, TextField } from '@mollybet/ui';
import Select from '@mollybet/ui/dist/components/Select';
import React, {
  ComponentProps,
  HTMLProps,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { ExpandingContainer } from '../expandingContainer';
import { Comparible } from './base';

// #region Types
/** Currently supported filter types */
export enum FilterTypes {
  FREE_TEXT,
  OPTIONS,
  DATE,
  DATETIME,
  NUMBER,
  BOOLEAN,
}

export type OptionValueType = string | number | boolean | null;
export type OptionLabelType = React.ReactNode | JSX.Element;
export interface OptionType<
  Value extends OptionValueType = OptionValueType,
  Label extends OptionLabelType = OptionLabelType
> {
  value: Value;
  label: Label;
}

export interface InputFilterProp<Row> {
  key: string;
  label?: string;
  type: FilterTypes;
  valueGenerator?: (row: Row) => Comparible;
  defaultValue?: string;
  inputProps?: Omit<
    HTMLProps<HTMLInputElement>,
    'onChange' | 'label' | 'value'
  >;
}

export interface NumberFilterProp<Row> extends InputFilterProp<Row> {
  type: FilterTypes.NUMBER;
  valueGenerator?: (row: Row) => number | undefined;
}

export interface DateFilterProp<Row> extends InputFilterProp<Row> {
  type: FilterTypes.DATE | FilterTypes.DATETIME;
  valueGenerator?: (row: Row) => number | string | Date | undefined;
}

export interface TextFilterProp<Row> extends InputFilterProp<Row> {
  type: FilterTypes.FREE_TEXT;
  valueGenerator?: (row: Row) => string | undefined;
}

export interface BooleanFilterProp<Row> extends InputFilterProp<Row> {
  type: FilterTypes.BOOLEAN;
  valueGenerator?: (row: Row) => boolean | undefined;
}

interface InputFilter<Row> {
  key: string;
  label?: string;
  type: FilterTypes;
  valueGenerator: (row: Row) => Comparible;
  defaultValue: string;
  inputProps: Omit<HTMLProps<HTMLInputElement>, 'onChange' | 'label' | 'value'>;
}

export interface NumberFilter<Row> extends InputFilter<Row> {
  type: FilterTypes.NUMBER;
  valueGenerator: (row: Row) => number | undefined;
}

export interface DateFilter<Row> extends InputFilter<Row> {
  type: FilterTypes.DATE | FilterTypes.DATETIME;
  valueGenerator: (row: Row) => number | string | Date | undefined;
}

export interface TextFilter<Row> extends InputFilter<Row> {
  type: FilterTypes.FREE_TEXT;
  valueGenerator: (row: Row) => string | undefined;
}

export interface BooleanFilter<Row> extends InputFilter<Row> {
  type: FilterTypes.BOOLEAN;
  valueGenerator: (row: Row) => boolean | undefined;
}

export interface OptionsFilterProp<Row> {
  key: string;
  label?: string;
  type: FilterTypes.OPTIONS;
  valueGenerator?: (row: Row) => Comparible;
  options: OptionType[];
  defaultOptions?: OptionType;
  inputProps?: Omit<
    ComponentProps<typeof Select>,
    'onChange' | 'label' | 'value'
  >;
}

interface OptionsFilter<Row> {
  key: string;
  label?: string;
  type: FilterTypes.OPTIONS;
  valueGenerator: (row: Row) => Comparible;
  options: OptionType[];
  defaultOptions: OptionType | undefined;
  inputProps: Omit<
    ComponentProps<typeof Select>,
    'onChange' | 'label' | 'value'
  >;
}

export type FilterProp<Row> =
  | NumberFilterProp<Row>
  | DateFilterProp<Row>
  | TextFilterProp<Row>
  | BooleanFilterProp<Row>
  | OptionsFilterProp<Row>;

export type Filter<Row> =
  | NumberFilter<Row>
  | DateFilter<Row>
  | TextFilter<Row>
  | BooleanFilter<Row>
  | OptionsFilter<Row>;

type FilterOrder = string[];

interface FilterMap<Row> {
  [key: string]: InputFilter<Row> | OptionsFilter<Row>;
}

export interface GLEFilterValue {
  gt?: string;
  lt?: string;
  eq?: string;
}

export interface TextFilterComponentHooks<Row> {
  value: string;
  filter: TextFilter<Row>;
  clearValue: () => void;
  setValue: (value: string) => void;
}

export interface OptionsFilterComponentHooks<Row> {
  value: OptionType | undefined;
  filter: OptionsFilter<Row>;
  clearValue: () => void;
  setValue: (value: OptionType) => void;
}

export interface GLEFilterComponentHooks<Row> {
  value: GLEFilterValue;
  filter: NumberFilter<Row> | DateFilter<Row>;
  clearValue: () => void;
  setValue: (value: GLEFilterValue) => void;
}

export interface BooleanFilterComponentHooks<Row> {
  value: boolean | undefined;
  filter: BooleanFilter<Row>;
  clearValue: () => void;
  setValue: (value: boolean) => void;
}

export type AnyComponentHooks<Row> =
  | TextFilterComponentHooks<Row>
  | OptionsFilterComponentHooks<Row>
  | GLEFilterComponentHooks<Row>
  | BooleanFilterComponentHooks<Row>;

export interface Hooks<Row> {
  [key: string]: AnyComponentHooks<Row>;
}

export interface Components {
  [key: string]:
    | ReturnType<typeof TextFilterComponent>
    | ReturnType<typeof OptionsFilterComponent>
    | ReturnType<typeof GLEFilterComponent>
    | ReturnType<typeof BooleanFilterComponent>;
}

const isOptionsFilterProp = <Row,>(
  filter: FilterProp<Row>
): filter is OptionsFilterProp<Row> => filter.type === FilterTypes.OPTIONS;

// const isOptionsFilter = <Row,>(
//   filter: InputFilter<Row> | OptionsFilter<Row>
// ): filter is OptionsFilter<Row> => filter.type === FilterTypes.OPTIONS;

export const isTextFilter = <Row,>(
  filter: Filter<Row>
): filter is TextFilter<Row> => filter.type === FilterTypes.FREE_TEXT;
export const isOptionsFilter = <Row,>(
  filter: Filter<Row>
): filter is OptionsFilter<Row> => filter.type === FilterTypes.OPTIONS;
export const isBooleanFilter = <Row,>(
  filter: Filter<Row>
): filter is BooleanFilter<Row> => filter.type === FilterTypes.BOOLEAN;
export const isNumberFilter = <Row,>(
  filter: Filter<Row>
): filter is NumberFilter<Row> => filter.type === FilterTypes.NUMBER;
export const isDateFilter = <Row,>(
  filter: Filter<Row>
): filter is DateFilter<Row> =>
  filter.type === FilterTypes.DATE || filter.type === FilterTypes.DATETIME;

export const isTextFilterComponentHooks = <Row,>(
  hook: AnyComponentHooks<Row>
): hook is TextFilterComponentHooks<Row> => isTextFilter(hook.filter);
export const isOptionsFilterComponentHooks = <Row,>(
  hook: AnyComponentHooks<Row>
): hook is OptionsFilterComponentHooks<Row> => isOptionsFilter(hook.filter);
export const isGLEFilterComponentHooks = <Row,>(
  hook: AnyComponentHooks<Row>
): hook is GLEFilterComponentHooks<Row> =>
  isNumberFilter(hook.filter) || isDateFilter(hook.filter);
export const isBooleanFilterComponentHooks = <Row,>(
  hook: AnyComponentHooks<Row>
): hook is BooleanFilterComponentHooks<Row> => isBooleanFilter(hook.filter);

/** Provides default values for filter properties that are optional. */
const createInputFilterFromProp = <Row,>(
  filter: InputFilterProp<Row>
): InputFilter<Row> => ({
  key: filter.key,
  label: filter.label,
  type: filter.type,
  valueGenerator: filter.valueGenerator || ((row: Row) => row[filter.key]),
  defaultValue: filter.defaultValue || '',
  inputProps: filter.inputProps || {},
});

/** Provides default values for filter properties that are optional. */
const createOptionsFilterFromProp = <Row,>(
  filter: OptionsFilterProp<Row>
): OptionsFilter<Row> => ({
  key: filter.key,
  label: filter.label,
  type: filter.type,
  valueGenerator: filter.valueGenerator || ((row: Row) => row[filter.key]),
  options: filter.options,
  defaultOptions: filter.defaultOptions || null,
  inputProps: filter.inputProps || {},
});

type TypeOfType =
  | 'string'
  | 'number'
  | 'bigint'
  | 'boolean'
  | 'symbol'
  | 'undefined'
  | 'object'
  | 'function';

/**
 * An extended version of typeof command providing more specifics when typeof
 * returns 'object'
 */
export const getType = (
  object: unknown
): TypeOfType | 'array' | 'date' | 'null' => {
  const simpleTypeOf = typeof object;
  if (simpleTypeOf !== 'object') return simpleTypeOf;
  if (Array.isArray(object)) return 'array';
  if (object === null) return 'null';
  if (object instanceof Date) return 'date';

  return simpleTypeOf;
};

const filterTypeMap: { [key in FilterTypes]: Set<ReturnType<typeof getType>> } =
  {
    [FilterTypes.FREE_TEXT]: new Set(['string']),
    [FilterTypes.OPTIONS]: new Set(['string', 'number', 'boolean']),
    [FilterTypes.DATE]: new Set(['string', 'number', 'date']),
    [FilterTypes.DATETIME]: new Set(['string', 'number', 'date']),
    [FilterTypes.NUMBER]: new Set(['number']),
    [FilterTypes.BOOLEAN]: new Set(['boolean']),
  };
// #endregion

// #region Elements
const LabeledFilter = styled.label`
  display: grid;
  grid-template-rows: auto auto;
  grid-template-areas: 'label' 'filter';
`;

const LabeledFilterLabel = styled.span`
  grid-area: label;
  font-size: 0.75rem;
  text-transform: uppercase;
`;

const LabeledFilterFilter = styled.span`
  grid-area: filter;
`;

const FilterContainerHeader = styled.div`
  height: 16px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  user-select: none;
  cursor: pointer;
  margin-bottom: 0.375rem;
`;

const FilterContainerTitle = styled.div`
  margin: 0 1rem 0 0.125rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: bold;
`;

const FilterContainerValue = styled.div<{ isHidden?: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 0 0.125rem 0 0.125rem;
  padding: 0.25rem 0.375rem;
  box-sizing: border-box;
  font-size: 0.5rem;
  letter-spacing: 0.5px;
  height: 16px;
  text-transform: uppercase;
  font-weight: bold;
  background-color: ${({ theme }) => theme.cardLevel1};
  border-radius: 2px;
  opacity: ${({ isHidden }) => (isHidden ? 0 : 1)};
  pointer-events: ${({ isHidden }) => (isHidden ? 'none' : undefined)};
  transition: opacity 300ms, background-color 300ms;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.cardLevel2};
  }

  span {
    margin-left: 0.125rem;
  }
`;
// #endregion

// #region Components
/** Defines behaviour for when filter type is free text. */
const compareFreeText = (filterValue?: string, rowValue?: string): boolean => {
  if (!filterValue) return true;
  if (rowValue == null) return false;
  return rowValue.toLowerCase().includes(filterValue.toLowerCase());
};

/** Defines behaviour for when filter type is boolean. */
const compareBoolean = (filterValue?: boolean, rowValue?: boolean): boolean => {
  if (filterValue == null) return true;
  if (rowValue == null) return false;
  return rowValue === filterValue;
};

/** Defines behaviour for when filter type is option. */
const compareOption = <Value extends OptionValueType = OptionValueType>(
  filterValue?: OptionType<Value>,
  rowValue?: Value
): boolean => {
  if (filterValue == null) return true;
  if (filterValue.value == null) return true;
  if (rowValue == null) return false;
  return rowValue === filterValue.value;
};

/** Defines behaviour for when filter type is number. */
const compareNumber = (
  filterValue?: GLEFilterValue,
  rowValue?: number
): boolean => {
  if (!filterValue?.gt && !filterValue?.lt && !filterValue?.eq) return true;
  if (rowValue == null) return false;
  if (filterValue?.eq != null && parseFloat(filterValue.eq) !== rowValue)
    return false;
  if (filterValue?.gt != null && parseFloat(filterValue.gt) > rowValue)
    return false;
  if (filterValue?.lt != null && parseFloat(filterValue.lt) < rowValue)
    return false;
  return true;
};

/** Defines behaviour for when filter type is date. */
const compareDate = (
  filterValue?: GLEFilterValue,
  rowValue?: Date | string | number
): boolean => {
  if (!filterValue?.gt && !filterValue?.lt && !filterValue?.eq) return true;
  if (rowValue == null) return false;
  if (
    filterValue?.eq != null &&
    new Date(filterValue.eq).toISOString().slice(0, 10) !==
      new Date(rowValue).toISOString().slice(0, 10)
  )
    return false;
  if (
    filterValue?.gt != null &&
    new Date(filterValue.gt).toISOString().slice(0, 10) >
      new Date(rowValue).toISOString().slice(0, 10)
  )
    return false;
  if (
    filterValue?.lt != null &&
    new Date(filterValue.lt).toISOString().slice(0, 10) <
      new Date(rowValue).toISOString().slice(0, 10)
  )
    return false;
  return true;
};

/** Defines behaviour for when filter type is datetime. */
const compareDatetime = (
  filterValue?: GLEFilterValue,
  rowValue?: Date | string | number
): boolean => {
  if (!filterValue?.gt && !filterValue?.lt && !filterValue?.eq) return true;
  if (rowValue == null) return false;
  if (
    filterValue?.eq != null &&
    new Date(filterValue.eq) !== new Date(rowValue)
  )
    return false;
  if (filterValue?.gt != null && new Date(filterValue.eq) > new Date(rowValue))
    return false;
  if (filterValue?.lt != null && new Date(filterValue.eq) < new Date(rowValue))
    return false;
  return true;
};

/**
 * Takes the filter type and returns the value of the relevant comparison
 * function.
 */
const compareType = (
  type: FilterTypes,
  filterValue?: string | boolean | OptionType | GLEFilterValue,
  rowValue?: Comparible
): boolean => {
  if (type === FilterTypes.FREE_TEXT)
    return compareFreeText(filterValue as string, rowValue as string);
  if (type === FilterTypes.OPTIONS)
    return compareOption(
      filterValue as OptionType,
      rowValue as OptionValueType
    );
  if (type === FilterTypes.DATE)
    return compareDate(
      filterValue as GLEFilterValue,
      rowValue as string | number | Date
    );
  if (type === FilterTypes.DATETIME)
    return compareDatetime(
      filterValue as GLEFilterValue,
      rowValue as string | number | Date
    );
  if (type === FilterTypes.NUMBER)
    return compareNumber(filterValue as GLEFilterValue, rowValue as number);
  if (type === FilterTypes.BOOLEAN)
    return compareBoolean(filterValue as boolean, rowValue as boolean);
  throw Error('Tried to compare unknown type');
};

/**
 * Debounced free text input, for use with useFiltration
 */
export const TextFilterComponent = <Row,>({
  value,
  filter,
  setValue,
}: Omit<TextFilterComponentHooks<Row>, 'clearValue'>) => {
  const debounceTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [localValue, setLocalValue] = useState(value);
  const debouncedOnChange = (value) => {
    setLocalValue(value);
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => setValue(value), 300);
  };
  useEffect(() => {
    clearTimeout(debounceTimeout.current);
    setLocalValue(value);
    return () => clearTimeout(debounceTimeout.current);
  }, [value]);
  return (
    <LabeledFilter>
      {filter.label && <LabeledFilterLabel>{filter.label}</LabeledFilterLabel>}
      <LabeledFilterFilter>
        <TextField
          value={localValue}
          onChange={(event) => debouncedOnChange(event.currentTarget.value)}
          {...filter.inputProps}
          shrinked
        />
      </LabeledFilterFilter>
    </LabeledFilter>
  );
};

/**
 * React select component, for use with useFiltration
 */
export const OptionsFilterComponent = <Row,>({
  value,
  filter,
  setValue,
}: Omit<OptionsFilterComponentHooks<Row>, 'clearValue'>) => {
  return (
    <LabeledFilter>
      {filter.label && <LabeledFilterLabel>{filter.label}</LabeledFilterLabel>}
      <LabeledFilterFilter>
        <Select
          // eslint-disable-next-line
          // @ts-ignore
          value={value}
          // eslint-disable-next-line
          // @ts-ignore
          options={filter.options}
          onChange={setValue}
          {...filter.inputProps}
          shrinked
        />
      </LabeledFilterFilter>
    </LabeledFilter>
  );
};

/**
 * Debounced tripple input (eq, gt, lt) that sets input[type] based on the
 * filter type, for use with useFiltration.
 *
 * Currently supports number, date, datetime.
 */
export const GLEFilterComponent = <Row,>({
  value,
  filter,
  setValue,
}: Omit<GLEFilterComponentHooks<Row>, 'clearValue'>) => {
  const debounceTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [localValue, setLocalValue] = useState(value);
  const debouncedOnChange = (value) => {
    setLocalValue(value);
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => setValue(value), 300);
  };
  useEffect(() => {
    clearTimeout(debounceTimeout.current);
    setLocalValue(value);
    return () => clearTimeout(debounceTimeout.current);
  }, [value]);

  const htmlFilterType =
    filter.type === FilterTypes.NUMBER
      ? 'number'
      : filter.type === FilterTypes.DATE
      ? 'date'
      : 'datetime-local';

  return (
    <LabeledFilter>
      {filter.label && <LabeledFilterLabel>{filter.label}</LabeledFilterLabel>}
      <LabeledFilterFilter>
        <TextField
          type={htmlFilterType}
          className="eq"
          shrinked
          value={localValue?.eq || ''}
          label="Equal"
          onChange={(event) =>
            debouncedOnChange({ ...value, eq: event.currentTarget.value })
          }
          {...filter.inputProps}
        />
        <TextField
          type={htmlFilterType}
          className="gt"
          shrinked
          value={localValue?.gt || ''}
          label="Greater Than "
          onChange={(event) =>
            debouncedOnChange({ ...value, gt: event.currentTarget.value })
          }
          {...filter.inputProps}
        />
        <TextField
          type={htmlFilterType}
          className="lt"
          value={localValue?.lt || ''}
          label="Less Than "
          onChange={(event) =>
            debouncedOnChange({ ...value, lt: event.currentTarget.value })
          }
          shrinked
          {...filter.inputProps}
        />
      </LabeledFilterFilter>
    </LabeledFilter>
  );
};

/** Yes/No buttons for boolean filter type, for use with useFiltration. */
export const BooleanFilterComponent = <Row,>({
  value,
  filter,
  setValue,
}: Omit<BooleanFilterComponentHooks<Row>, 'clearValue'>) => {
  return (
    <LabeledFilter>
      {filter.label && <LabeledFilterLabel>{filter.label}</LabeledFilterLabel>}
      <LabeledFilterFilter>
        <Button
          className="yes"
          variant={value === true ? 'primary' : 'secondary'}
          onClick={() => setValue(value === true ? undefined : true)}
        >
          Yes
        </Button>
        <Button
          className="no"
          variant={value === false ? 'primary' : 'secondary'}
          onClick={() => setValue(value === false ? undefined : false)}
        >
          No
        </Button>
      </LabeledFilterFilter>
    </LabeledFilter>
  );
};

/**
 * A handy contatiner element for filters which will expand and contract and
 * display filters event when contracted
 */
export const FiltersContainer = <Row,>({
  hooks,
  title,
  children,
}: React.PropsWithChildren<{
  hooks?: Hooks<Row>;
  title?: React.ReactNode;
}>) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  return (
    <ExpandingContainer visible={isFiltersOpen} minHeight={16} animate>
      <FilterContainerHeader onClick={() => setIsFiltersOpen(!isFiltersOpen)}>
        <Icon
          path={isFiltersOpen ? mdiChevronUp : mdiChevronDown}
          size={0.675}
        />
        <FilterContainerTitle>{title || 'Filters'}</FilterContainerTitle>
        {hooks &&
          Object.values(hooks).map((hook) =>
            isOptionsFilterComponentHooks(hook) && hook.value?.value != null ? (
              <FilterContainerValue
                key={hook.filter.key}
                isHidden={isFiltersOpen}
                onClick={(event) => {
                  hook.clearValue();
                  event.stopPropagation();
                }}
              >
                <Icon path={mdiClose} size={0.375} />
                <span>
                  {hook.filter.label && `${hook.filter.label}: `}
                  {hook.value.label}
                </span>
              </FilterContainerValue>
            ) : isGLEFilterComponentHooks(hook) &&
              (hook.value?.eq || hook.value?.gt || hook.value?.lt) ? (
              <FilterContainerValue
                key={hook.filter.key}
                isHidden={isFiltersOpen}
                onClick={(event) => {
                  hook.clearValue();
                  event.stopPropagation();
                }}
              >
                <Icon path={mdiClose} size={0.375} />
                <span>
                  {hook.value?.eq
                    ? `${hook.filter.label || hook.filter.key} = ${
                        hook.value.eq
                      }`
                    : hook.value?.gt && hook.value?.lt
                    ? `${hook.value.gt} < ${
                        hook.filter.label || hook.filter.key
                      } < ${hook.value.lt}`
                    : hook.value?.gt
                    ? `${hook.filter.label || hook.filter.key} > ${
                        hook.value.gt
                      }`
                    : `${hook.filter.label || hook.filter.key} < ${
                        hook.value.lt
                      }`}
                </span>
              </FilterContainerValue>
            ) : !(
                isOptionsFilterComponentHooks(hook) ||
                isGLEFilterComponentHooks(hook)
              ) &&
              hook.value != null &&
              hook.value !== '' ? (
              <FilterContainerValue
                key={hook.filter.key}
                isHidden={isFiltersOpen}
                onClick={(event) => {
                  hook.clearValue();
                  event.stopPropagation();
                }}
              >
                <Icon path={mdiClose} size={0.375} />
                <span>
                  {hook.filter.label && `${hook.filter.label}: `}
                  {hook.value}
                </span>
              </FilterContainerValue>
            ) : null
          )}
      </FilterContainerHeader>
      {children}
    </ExpandingContainer>
  );
};

/**
 * Takes a list of rows and a list of filter objects and generates a
 * filtered list of rows and map of components that allow the users to change
 * the filter values.
 */
export const useFiltration = <Row,>(
  rows: Row[],
  filters: FilterProp<Row>[]
): [Row[], Components, Hooks<Row>] => {
  const [filterValues, setFilterValues] = useState<{
    [key: string]: string | OptionType | boolean | GLEFilterValue;
  }>(
    filters.reduce((acc, filter) => {
      if (isOptionsFilterProp(filter)) {
        acc[filter.key] = filter.defaultOptions || undefined;
      } else {
        acc[filter.key] = filter.defaultValue || '';
      }

      return acc;
    }, {})
  );

  /**
   * Keeps the specified order of filters so they dont jump around between
   * renders.
   */
  const filterOrder = useMemo(
    (): FilterOrder => filters.map((filter) => filter.key),
    [filters]
  );

  /** Maps filter key to filter object for quick access. */
  const filterMap = useMemo(
    (): FilterMap<Row> =>
      filters.reduce(
        (acc, filter) => ({
          ...acc,
          [filter.key]: isOptionsFilterProp(filter)
            ? createOptionsFilterFromProp(filter)
            : createInputFilterFromProp(filter),
        }),
        {}
      ),
    [filters]
  );

  /**
   * Check to make sure rows have an appropriate comparison value for the
   * filter type.
   *
   * Preferably typescript would do this for us but its unclear how to do this.
   */
  useEffect(() => {
    Object.values(filterMap).forEach((filter) => {
      const rowTypes = rows.reduce((acc, row) => {
        acc.add(getType(filter.valueGenerator(row)));
        return acc;
      }, new Set<ReturnType<typeof getType>>());
      rowTypes.delete('undefined');
      rowTypes.delete('null');

      const filterTypes = filterTypeMap[filter.type];
      if (!Array.from(rowTypes).every((type) => filterTypes.has(type))) {
        console.error(
          'Filter found where valueGenerator returns incomparible type, this ' +
            'will cause the filter to always return false on affected rows.',
          '\nRow types:',
          rowTypes,
          '\nFilter types:',
          filterTypes,
          '\nFilter:',
          filter
        );
      }
    });
  }, [filterMap, rows]);

  /** Applies filter values to the provided rows. */
  const filteredRows = useMemo((): Row[] => {
    return rows.filter((row: Row) => {
      for (const key of filterOrder) {
        const filter = filterMap[key];
        if (
          !compareType(
            filter.type,
            filterValues[key],
            filter.valueGenerator(row)
          )
        )
          return false;
      }
      return true;
    });
  }, [rows, filterOrder, filterMap, filterValues]);

  /**
   * Provides hooks for various parts incase people want to implement their own
   * filter elements.
   */
  const hooks = useMemo(
    (): Hooks<Row> =>
      Object.values(filterMap).reduce(
        (acc, filter) => ({
          ...acc,
          [filter.key]: {
            filter,
            value: filterValues[filter.key],
            setValue: (value) =>
              setFilterValues({ ...filterValues, [filter.key]: value }),
            clearValue: () =>
              setFilterValues({ ...filterValues, [filter.key]: undefined }),
          },
        }),
        {}
      ),
    [filterMap, filterValues]
  );

  /**
   * Generates a map of filter key to filter components to be used to allow the
   * use to set the filter values.
   */
  const components = useMemo(
    (): Components =>
      Object.values(hooks).reduce((acc, hook) => {
        if (isOptionsFilterComponentHooks(hook)) {
          acc[hook.filter.key] = (
            <OptionsFilterComponent
              value={hook.value}
              filter={hook.filter}
              setValue={hook.setValue}
            />
          );
        }
        if (isBooleanFilterComponentHooks(hook)) {
          acc[hook.filter.key] = (
            <BooleanFilterComponent
              value={hook.value}
              filter={hook.filter}
              setValue={hook.setValue}
            />
          );
        }
        if (isGLEFilterComponentHooks(hook)) {
          acc[hook.filter.key] = (
            <GLEFilterComponent
              value={hook.value}
              filter={hook.filter}
              setValue={hook.setValue}
            />
          );
        }
        if (isTextFilterComponentHooks(hook)) {
          acc[hook.filter.key] = (
            <TextFilterComponent
              value={hook.value}
              filter={hook.filter}
              setValue={hook.setValue}
            />
          );
        }
        return acc;
      }, {}),
    [hooks]
  );

  return [filteredRows, components, hooks];
};
// #endregion
